import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AdminRole } from '@insurensa/shared';
import { api, setAccessToken } from './api.js';

export interface AdminIdentity {
  id: string;
  name: string;
  username: string;
  email: string;
  role: AdminRole;
}

interface AuthState {
  admin: AdminIdentity | null;
  /** True until the initial silent refresh has settled. */
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Whether the signed-in operator holds any of the given roles. */
  can: (...roles: AdminRole[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * On load, try to exchange the HttpOnly refresh cookie for an access token.
   * This is what keeps a reload signed in without ever persisting a token where
   * script can read it.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { accessToken } = await api.post<{ accessToken: string }>('/auth/refresh');
        if (cancelled) return;
        setAccessToken(accessToken);
        const me = await api.get<AdminIdentity>('/admin/me');
        if (!cancelled) setAdmin(me);
      } catch {
        if (!cancelled) {
          setAccessToken(null);
          setAdmin(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // The API client raises this when a refresh fails mid-session.
  useEffect(() => {
    const onExpired = (): void => setAdmin(null);
    window.addEventListener('insurensa:session-expired', onExpired);
    return () => window.removeEventListener('insurensa:session-expired', onExpired);
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await api.post<{ accessToken: string; admin: AdminIdentity }>(
      '/auth/admin/login',
      { username, password },
    );
    setAccessToken(result.accessToken);
    setAdmin(result.admin);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setAdmin(null);
    }
  }, []);

  const can = useCallback(
    (...roles: AdminRole[]) => (admin ? roles.includes(admin.role) : false),
    [admin],
  );

  const value = useMemo<AuthState>(
    () => ({ admin, loading, signIn, signOut, can }),
    [admin, loading, signIn, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
